import React, { useState } from "react";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

const AVATARS = [
    "https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
    "https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80",
    "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
];

const AVENOR_LOGO = `${process.env.PUBLIC_URL}/avenor.png`;

const WHY_NOW_POINTS = [
    "Traditional tools are slow, manual, and disconnected",
    "Most teams use 5-7 tools just to manage people and work",
    "Remote and hybrid work has changed everything",
    "AI is powerful only when it is embedded into real workflows",
    "You cannot scale chaos. You can scale systems",
];

const PRICE_COMPARE_ITEMS = [
    {
        function: "Team Chat and Internal Comms",
        typicalTool: "Slack / Teams",
        typicalCost: "GBP 10-15",
        insightos: "Live chat and social feed",
        icon: "💬",
    },
    {
        function: "HR Records and Policies",
        typicalTool: "Breathe / CharlieHR",
        typicalCost: "GBP 5-8",
        insightos: "HR and policy manager",
        icon: "📋",
    },
    {
        function: "Time and Attendance",
        typicalTool: "Clockify / Deputy",
        typicalCost: "GBP 4-6",
        insightos: "Clock-in with geo tracking",
        icon: "⏰",
    },
    {
        function: "Task and Project Tracking",
        typicalTool: "Asana / Monday",
        typicalCost: "GBP 8-12",
        insightos: "Tasks, Kanban and Gantt",
        icon: "📊",
    },
    {
        function: "Training and Onboarding",
        typicalTool: "Trainual",
        typicalCost: "GBP 6-10",
        insightos: "AI training docs and quizzes",
        icon: "🎓",
    },
    {
        function: "Recruitment Tools",
        typicalTool: "ATS / CV Tools",
        typicalCost: "GBP 5-10",
        insightos: "CV scanner and interview tools",
        icon: "👥",
    },
];

const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, ease: "easeOut" }
    }
};

const sectionVariants = {
    hidden: { opacity: 0, y: 14 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.35, ease: "easeOut" }
    },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
};

function getSliderGradient() {
    return `linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #a21caf 100%)`;
}

function getCircleSize(price) {
    const digits = price.toFixed(2).length;
    return Math.min(200, 80 + (digits - 4) * 20);
}

function getAnnualPricing(userCount) {
    const firstTierUsers = Math.min(userCount, 10);
    const secondTierUsers = Math.max(userCount - 10, 0);
    const total = (firstTierUsers * 500) + (secondTierUsers * 600);

    const breakdown = secondTierUsers > 0
        ? `${firstTierUsers} users x £500 + ${secondTierUsers} users x £600 = £${total.toLocaleString(undefined, { minimumFractionDigits: 2 })} Per Year`
        : `${firstTierUsers} user${firstTierUsers > 1 ? "s" : ""} x £500.00 = £${total.toLocaleString(undefined, { minimumFractionDigits: 2 })} Per Year`;

    return {
        total,
        breakdown,
    };
}

export default function Pricing() {
    const [people, setPeople] = useState(1);
    const [billingCycle, setBillingCycle] = useState("monthly");
    const location = useLocation();
    const companyCode = location.state?.companyCode || "";

    const annualPricing = getAnnualPricing(people);
    const annualPrice = annualPricing.total;
    const price = billingCycle === "annual" ? annualPrice : people * 50;
    const priceStr = price.toLocaleString(undefined, { minimumFractionDigits: 2 });
    const circleSize = getCircleSize(price);

    const avatarCount = Math.min(people, 10);
    const avatars = [];
    for (let i = 0; i < avatarCount; i++) {
        avatars.push(
            <img
                key={i}
                alt=""
                src={AVATARS[i % AVATARS.length]}
                className="inline-block w-10 h-10 rounded-full ring-2 ring-white"
                style={{ marginLeft: i === 0 ? 0 : 8 }}
            />
        );
    }
    if (people > 10) {
        avatars.push(
            <div
                key="more"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-700 font-bold ring-2 ring-white"
                style={{ marginLeft: 8 }}
            >
                +{people - 10}
            </div>
        );
    }

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (billingCycle === "annual") {
            alert("Annual checkout is coming soon. Please use Monthly for now.");
            return;
        }

        try {
            const response = await fetch("https://api.insightos.co.uk/api/checkout-trial-conversion", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    quantity: people,
                    qualification: {
                        companycode: companyCode,
                        billingCycle: "monthly"
                    }
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to create Stripe checkout session");
            }

            const session = await response.json();
            if (!session?.url) {
                throw new Error("Missing checkout URL");
            }

            window.location.assign(session.url);
        } catch (err) {
            alert("Failed to submit. Please try again.");
        }
    };

    return (
        <>
            <div className="w-full sticky top-0 z-50">
                <div className="flex items-center gap-x-6 bg-gradient-to-r from-sky-700 via-indigo-700 to-blue-800 px-6 py-2.5 sm:px-3.5 sm:before:flex-1 shadow-lg">
                    <p className="text-sm/6 text-white">
                        <a href="#">
                            <strong className="font-semibold">Trial Conversion</strong>
                            <svg viewBox="0 0 2 2" aria-hidden="true" className="mx-2 inline size-0.5 fill-current">
                                <circle r={1} cx={1} cy={1} />
                            </svg>
                            <span className="font-semibold">Standard pricing applies</span> for company code <span className="font-bold">{companyCode}</span>
                        </a>
                    </p>
                    <div className="flex flex-1 justify-end">
                        <button type="button" className="-m-3 p-3 focus-visible:-outline-offset-4">
                            <span className="sr-only">Dismiss</span>
                            <XMarkIcon aria-hidden="true" className="size-5 text-white" />
                        </button>
                    </div>
                </div>
            </div>
            <motion.div
                className="relative w-full min-h-screen overflow-hidden bg-gradient-to-b from-sky-50 via-indigo-50 to-cyan-50 py-6 sm:py-10"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="absolute -top-20 left-[-100px] h-64 w-64 rounded-full bg-cyan-300/35 blur-3xl" />
                <div className="absolute right-[-80px] top-28 h-72 w-72 rounded-full bg-indigo-300/35 blur-3xl" />

                <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6">
                    <motion.div
                        className="mb-6 rounded-3xl border border-white/70 bg-white/75 p-6 shadow-[0_20px_50px_rgba(30,64,175,0.16)] backdrop-blur-xl"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <img src="/1.png" alt="Logo" className="h-14 w-14 rounded-xl object-cover ring-4 ring-white/80" />
                                <div>
                                    <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Trial Conversion Pricing</h1>
                                    <p className="text-sm text-slate-600 sm:text-base">Choose your billing cycle and set the user count for your team.</p>
                                </div>
                            </div>
                            <div className="rounded-xl bg-gradient-to-r from-indigo-600 to-sky-600 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white">
                                Company: {companyCode || "N/A"}
                            </div>
                        </div>
                    </motion.div>

                    <motion.section
                        className="mb-6 rounded-3xl border border-white/70 bg-white/75 p-6 shadow-[0_20px_50px_rgba(30,64,175,0.16)] backdrop-blur-xl"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                            The Future of Work Is Already Here
                        </h2>
                        <p className="mt-2 text-sm font-semibold text-indigo-700 sm:text-base">
                            Old systems were not built for AI-driven teams. InsightOS is.
                        </p>

                        <div className="mt-4 rounded-2xl border border-white/80 bg-white/70 p-4 shadow-[0_12px_28px_rgba(15,23,42,0.1)]">
                            <ul className="space-y-2 text-sm text-slate-700 sm:text-base">
                                {WHY_NOW_POINTS.map((point, i) => (
                                    <motion.li
                                        key={point}
                                        initial={{ opacity: 0, x: -16 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.05 * i, duration: 0.25 }}
                                        className="flex items-start gap-2"
                                    >
                                        <span className="mt-1 text-indigo-600">•</span>
                                        <span>{point}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>

                        <p className="mt-4 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-sky-50 px-4 py-3 text-sm font-medium text-slate-800 sm:text-base">
                            InsightOS gives you the structure to make AI work for your team today, not six months from now.
                        </p>
                    </motion.section>

                    <motion.section
                        className="mb-6 rounded-3xl border border-white/70 bg-white/75 p-6 shadow-[0_20px_50px_rgba(30,64,175,0.16)] backdrop-blur-xl"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <div className="mb-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Value That Makes Sense</p>
                            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Why Our Price Point Is Justified</h2>
                            <p className="mt-2 text-sm text-slate-600 sm:text-base">
                                Replace multiple disconnected tools with one platform designed for operations, HR, and collaboration.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {PRICE_COMPARE_ITEMS.map((item, i) => (
                                <motion.div
                                    key={item.function}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.25 }}
                                    whileHover={{ y: -2, scale: 1.01 }}
                                    className="rounded-2xl border border-white/80 bg-white/80 p-4 shadow-[0_12px_28px_rgba(15,23,42,0.1)]"
                                >
                                    <div className="mb-2 flex items-center gap-2">
                                        <span className="text-xl">{item.icon}</span>
                                        <h3 className="text-sm font-bold text-slate-900 sm:text-base">{item.function}</h3>
                                    </div>

                                    <div className="mb-2 rounded-xl border border-rose-200 bg-rose-50/85 px-3 py-2">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">Typical Tool</p>
                                        <p className="text-xs text-slate-700 sm:text-sm">{item.typicalTool}</p>
                                        <p className="text-sm font-bold text-rose-700 sm:text-base">{item.typicalCost}</p>
                                    </div>

                                    <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-100 to-sky-50 px-3 py-2">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Included in InsightOS</p>
                                        <p className="text-xs font-semibold text-indigo-900 sm:text-sm">{item.insightos}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25, duration: 0.3 }}
                            className="mt-5 rounded-2xl border border-white/35 bg-gradient-to-r from-indigo-950 via-blue-700 to-indigo-700 p-4 text-white shadow-[0_16px_36px_rgba(30,64,175,0.22)]"
                        >
                            <p className="text-sm font-semibold sm:text-base">Typical stack: GBP 60-100 per user / month</p>
                            <p className="mt-1 text-lg font-extrabold sm:text-2xl">InsightOS: GBP 50 per user / month</p>
                        </motion.div>
                    </motion.section>

                    <motion.div
                        className="mb-5 flex w-full max-w-xl rounded-2xl border border-indigo-100 bg-white p-1.5 shadow"
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <button
                            type="button"
                            onClick={() => setBillingCycle("monthly")}
                            className={`w-1/2 rounded-xl px-4 py-2.5 text-sm font-bold transition ${billingCycle === "monthly"
                                ? "bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow"
                                : "text-slate-700 hover:bg-slate-100"
                                }`}
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            onClick={() => setBillingCycle("annual")}
                            className={`w-1/2 rounded-xl px-4 py-2.5 text-sm font-bold transition ${billingCycle === "annual"
                                ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow"
                                : "text-slate-700 hover:bg-slate-100"
                                }`}
                        >
                            Annual
                        </button>
                    </motion.div>

                    <form
                        className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_24px_55px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:p-8"
                        onSubmit={handleSubmit}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={billingCycle}
                                variants={sectionVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <div className="mb-6 flex w-full justify-center sm:mb-8">
                                    <motion.div
                                        className={`flex items-center justify-center border-4 border-white font-extrabold text-white shadow-xl ${billingCycle === "monthly"
                                            ? "bg-gradient-to-tr from-indigo-500 via-blue-600 to-sky-600"
                                            : "bg-gradient-to-tr from-emerald-500 via-teal-600 to-cyan-600"
                                            }`}
                                        style={{
                                            width: "100%",
                                            maxWidth: `${circleSize}px`,
                                            height: `${circleSize}px`,
                                            borderRadius: "9999px",
                                            fontSize: `${Math.max(20, circleSize / 5)}px`,
                                        }}
                                        initial={{ scale: 0.85, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                    >
                                        £{priceStr}
                                    </motion.div>
                                </div>

                                <h2 className="mb-3 text-xl font-bold text-slate-800 sm:mb-4 sm:text-2xl">Select Number of Users</h2>
                                <div className="mb-4 flex flex-wrap gap-2">{avatars}</div>
                                <div className="mb-2">
                                    <input
                                        type="range"
                                        min={1}
                                        max={1000}
                                        value={people}
                                        onChange={e => setPeople(Number(e.target.value))}
                                        className="h-3 w-full cursor-pointer appearance-none rounded-lg outline-none"
                                        style={{ background: getSliderGradient() }}
                                    />
                                </div>
                                <div className="mb-4 flex justify-between font-mono text-xs text-slate-400">
                                    <span>1</span>
                                    <span>25</span>
                                    <span>50</span>
                                    <span>75</span>
                                    <span>100</span>
                                    <span>200</span>
                                    <span>500</span>
                                    <span>1000</span>
                                </div>

                                <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm text-slate-700 sm:text-base">
                                    {billingCycle === "monthly" ? (
                                        <>
                                            {people} user{people > 1 ? "s" : ""} x £50.00 = <span className="font-bold text-indigo-700">£{priceStr} Per Month</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-bold text-emerald-700">{annualPricing.breakdown}</span>
                                        </>
                                    )}
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        <motion.button
                            type="submit"
                            whileHover={{ y: -2, scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            className={`w-full rounded-xl py-3 text-lg font-semibold text-white shadow transition ${billingCycle === "monthly"
                                ? "bg-gradient-to-r from-emerald-600 to-teal-700"
                                : "bg-gradient-to-r from-emerald-600 to-teal-600"
                                }`}
                        >
                            {billingCycle === "monthly" ? `Buy Monthly (£${priceStr})` : `Annual Coming Soon (£${priceStr})`}
                        </motion.button>

                        <motion.div
                            className="mt-8 overflow-hidden rounded-2xl border border-cyan-100 bg-white"
                            variants={sectionVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <div className="bg-gradient-to-r from-sky-600 via-indigo-600 to-blue-700 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white">
                                Included Features
                            </div>
                            <table className="w-full text-left text-sm">
                                <tbody>
                                    <tr className="border-b border-slate-200">
                                        <td className="px-4 py-3 font-semibold text-slate-800">All Features Unlocked</td>
                                        <td className="px-4 py-3 text-right text-2xl font-bold text-emerald-600">✓</td>
                                    </tr>
                                    <tr className="border-b border-slate-200">
                                        <td className="px-4 py-3 font-semibold text-slate-800">
                                            {billingCycle === "annual" ? "$600.00 Free AI Credits Per Year" : "$50.00 Free AI Credits Per Month"}
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-emerald-700">Included</td>
                                    </tr>
                                    <tr className="border-b border-slate-200">
                                        <td className="px-4 py-3 font-semibold text-slate-800">24/7 Technical Support</td>
                                        <td className="px-4 py-3 text-right font-bold text-emerald-700">Included</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-3 font-semibold text-slate-800">
                                            <div className="flex items-center gap-2">
                                                <img src={AVENOR_LOGO} alt="Avenor" className="h-6 w-auto" />
                                                <span>
                                                    {billingCycle === "monthly"
                                                        ? "Monthly: Free InsightOS Avenor Account with 35 Credits"
                                                        : "Annual: Free InsightOS Avenor Account with 100 Credits"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-emerald-700">Included</td>
                                    </tr>
                                </tbody>
                            </table>
                        </motion.div>
                    </form>
                </div>
            </motion.div>
        </>
    );
}
